import { useState } from "preact/hooks";
import {
  getMovieTorrents,
  getSeriesTorrents,
  searchCinemeta,
  getSeriesMeta,
  getRdApiKey,
} from "./api.ts";
import type {
  TorrentStream,
  CinemetaMeta,
  CinemetaVideo,
} from "./api.ts";
import { appStore } from "./store.ts";

export function SearchView() {
  const [mode, setMode] = useState<"movie" | "series">("movie");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CinemetaMeta[]>([]);
  const [selected, setSelected] = useState<CinemetaMeta | null>(null);
  const [streams, setStreams] = useState<TorrentStream[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [error, setError] = useState("");

  // Series-specific state
  const [episodes, setEpisodes] = useState<CinemetaVideo[]>([]);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number>(1);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<CinemetaVideo | null>(
    null
  );

  async function handleSearch() {
    if (!query.trim()) return;
    setSearching(true);
    setError("");
    setResults([]);
    setSelected(null);
    setStreams([]);
    setEpisodes([]);
    setSeasons([]);
    setSelectedEpisode(null);
    try {
      const metas = await searchCinemeta(query, mode);
      setResults(metas);
    } catch (e: any) {
      setError(e.message || String(e));
    }
    setSearching(false);
  }

  async function handleSelectMovie(movie: CinemetaMeta) {
    setSelected(movie);
    setStreams([]);
    setLoadingStreams(true);
    setError("");
    try {
      if (!getRdApiKey()) {
        throw new Error("Real-Debrid API key not set. Go to Settings.");
      }
      const result = await getMovieTorrents(movie.imdb_id);
      setStreams(result);
    } catch (e: any) {
      setError(e.message || String(e));
    }
    setLoadingStreams(false);
  }

  async function handleSelectSeries(series: CinemetaMeta) {
    setSelected(series);
    setEpisodes([]);
    setSeasons([]);
    setStreams([]);
    setSelectedEpisode(null);
    setLoadingMeta(true);
    setError("");
    try {
      const meta = await getSeriesMeta(series.imdb_id);
      const vids = (meta.videos ?? []).filter((v) => v.season > 0);
      setEpisodes(vids);
      const uniqueSeasons = [...new Set(vids.map((v) => v.season))].sort(
        (a, b) => a - b
      );
      setSeasons(uniqueSeasons);
      if (uniqueSeasons.length > 0) {
        setSelectedSeason(uniqueSeasons[uniqueSeasons.length - 1]!);
      }
    } catch (e: any) {
      setError(e.message || String(e));
    }
    setLoadingMeta(false);
  }

  async function handleSelectEpisode(ep: CinemetaVideo) {
    setSelectedEpisode(ep);
    setStreams([]);
    setLoadingStreams(true);
    setError("");
    try {
      if (!getRdApiKey()) {
        throw new Error("Real-Debrid API key not set. Go to Settings.");
      }
      const result = await getSeriesTorrents(
        selected!.imdb_id,
        ep.season,
        ep.number
      );
      setStreams(result);
    } catch (e: any) {
      setError(e.message || String(e));
    }
    setLoadingStreams(false);
  }

  function handleBack() {
    if (selectedEpisode) {
      setSelectedEpisode(null);
      setStreams([]);
      setError("");
    } else {
      setSelected(null);
      setStreams([]);
      setEpisodes([]);
      setSeasons([]);
      setSelectedEpisode(null);
      setError("");
    }
  }

  function handleModeChange(newMode: "movie" | "series") {
    setMode(newMode);
    setResults([]);
    setSelected(null);
    setStreams([]);
    setEpisodes([]);
    setSeasons([]);
    setSelectedEpisode(null);
    setError("");
  }

  const seasonEpisodes = episodes
    .filter((v) => v.season === selectedSeason)
    .sort((a, b) => a.number - b.number);

  // ── Search & results list ──
  if (!selected) {
    return (
      <div>
        {error && <p style={{ color: "red" }}>{error}</p>}

        <div style={{ marginBottom: "8px" }}>
          <label>
            <input
              type="radio"
              name="mode"
              checked={mode === "movie"}
              onChange={() => handleModeChange("movie")}
            />{" "}
            Movie
          </label>
          <label style={{ marginLeft: "12px" }}>
            <input
              type="radio"
              name="mode"
              checked={mode === "series"}
              onChange={() => handleModeChange("series")}
            />{" "}
            Series
          </label>
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
          <input
            placeholder={
              mode === "movie" ? "Search movies..." : "Search series..."
            }
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ flex: 1, padding: "8px", boxSizing: "border-box" }}
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            style={{ padding: "8px 16px" }}
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {results.length > 0 && (
          <div>
            <h3>Results ({results.length})</h3>
            {results.map((m) => (
              <div
                key={m.id}
                onClick={() =>
                  mode === "movie"
                    ? handleSelectMovie(m)
                    : handleSelectSeries(m)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
              >
                {m.poster && (
                  <img
                    src={m.poster}
                    alt={m.name}
                    style={{
                      width: "40px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: "bold" }}>{m.name}</div>
                  {m.releaseInfo && (
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {m.releaseInfo}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Selected header + back button (shared) ──
  const header = (
    <>
      <button
        onClick={handleBack}
        style={{
          padding: "4px 12px",
          marginBottom: "12px",
          border: "1px solid #333",
          background: "#fff",
          color: "#333",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        {mode === "series" && selectedEpisode
          ? "← Back to episodes"
          : "← Back to results"}
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {selected.poster && (
          <img
            src={selected.poster}
            alt={selected.name}
            style={{
              width: "60px",
              height: "90px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        )}
        <div>
          <h3 style={{ margin: 0 }}>
            {selected.name}
            {selectedEpisode &&
              ` — S${selectedEpisode.season}E${selectedEpisode.number}`}
          </h3>
          {selectedEpisode && (
            <div style={{ fontSize: "13px", marginTop: "2px" }}>
              {selectedEpisode.name}
            </div>
          )}
          {selected.releaseInfo && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              {selected.releaseInfo}
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ── Streams list (shared between movie & episode) ──
  const streamsList = (
    <>
      {loadingStreams && <p>Loading streams...</p>}
      {streams.length > 0 && (
        <div>
          <h3>Streams ({streams.length})</h3>
          {streams.map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ flex: 1, fontSize: "14px", minWidth: 0 }}>
                <div>{s.title}</div>
                {s.url && (
                  <div style={{ fontSize: "11px", color: "#999", marginTop: "2px", wordBreak: "break-all" }}>
                    {s.url}
                  </div>
                )}
              </div>
              {s.url && (
                <button
                  onClick={() => appStore.getState().playStream(s)}
                  style={{
                    marginLeft: "8px",
                    padding: "4px 12px",
                    border: "1px solid #333",
                    background: "#fff",
                    color: "#333",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Play
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  // ── Movie detail view ──
  if (mode === "movie") {
    return (
      <div>
        {error && <p style={{ color: "red" }}>{error}</p>}
        {header}
        {streamsList}
      </div>
    );
  }

  // ── Series detail view ──
  // If an episode is selected, show its streams
  if (selectedEpisode) {
    return (
      <div>
        {error && <p style={{ color: "red" }}>{error}</p>}
        {header}
        {streamsList}
      </div>
    );
  }

  // Otherwise show season picker + episode list
  return (
    <div>
      {error && <p style={{ color: "red" }}>{error}</p>}
      {header}

      {loadingMeta && <p>Loading episodes...</p>}

      {seasons.length > 0 && (
        <div>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ marginRight: "8px" }}>Season:</label>
            <select
              value={selectedSeason}
              onChange={(e) =>
                setSelectedSeason(Number((e.target as HTMLSelectElement).value))
              }
              style={{ padding: "6px 8px" }}
            >
              {seasons.map((s) => (
                <option key={s} value={s}>
                  Season {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            {seasonEpisodes.map((ep) => (
              <div
                key={ep.id}
                onClick={() => handleSelectEpisode(ep)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "28px",
                    textAlign: "right",
                    fontWeight: "bold",
                    color: "#666",
                    flexShrink: 0,
                  }}
                >
                  {ep.number}
                </div>
                <div style={{ flex: 1 }}>
                  <div>{ep.name}</div>
                  {ep.overview && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "2px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {ep.overview}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
